import type * as React from "react";

import { cn } from "../lib/cn";

export type SegmentedControlSize = "sm" | "md";

/** `ghost` has no resting surface, for a segment set inside a band of other controls. */
export type SegmentedControlVariant = "outline" | "ghost";

const sizeClasses: Record<SegmentedControlSize, string> = {
  sm: "px-2 py-1 text-xs",
  md: "px-2.5 py-1 text-sm",
};

// `outline` keeps an ink-coloured border when chosen so selection doesn't shift widths by 2px.
const variantClasses: Record<
  SegmentedControlVariant,
  { chosen: string; resting: string; hover: string }
> = {
  outline: {
    chosen: "border border-ink bg-ink text-ink-inverse",
    resting: "border border-rule bg-card text-ink",
    hover: "hover:-translate-y-px hover:shadow-lift-lg",
  },
  ghost: {
    chosen: "bg-ink text-ink-inverse",
    resting: "text-annotation",
    // Nothing to lift, so an ink wash: the same exception as Button's ghost.
    hover: "hover:bg-smudge hover:text-ink",
  },
};

export interface SegmentedControlOption<T extends string | number = string> {
  value: T;
  label: React.ReactNode;
  disabled?: boolean;
}

export interface SegmentedControlProps<
  T extends string | number = string,
> extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "role" | "onChange" | "children" | "aria-label"
> {
  /** Visible label only; the accessible name still comes from `aria-label`. */
  label?: string;
  /** Two to four; more is a `Select`. */
  options: readonly SegmentedControlOption<T>[];
  /** A value matching no option chooses nothing. */
  value: T;
  onValueChange: (value: T) => void;
  size?: SegmentedControlSize;
  variant?: SegmentedControlVariant;
  mono?: boolean;
  "aria-label": string;
  ref?: React.Ref<HTMLDivElement>;
}

/**
 * One exclusive choice as an APG `radiogroup` (not `aria-pressed` buttons,
 * which are for multi-select chips). Roving tabindex is local rather than
 * `useListboxNav`, whose active-descendant model decouples moving from choosing.
 */
export function SegmentedControl<T extends string | number = string>({
  label,
  options,
  value,
  onValueChange,
  size = "md",
  variant = "outline",
  mono = false,
  className,
  ...rest
}: SegmentedControlProps<T>) {
  const chosenIndex = options.findIndex((option) => option.value === value);
  // A disabled or missing choice can't hold the tab stop; fall back to the first enabled segment.
  const chosenOption = chosenIndex === -1 ? undefined : options[chosenIndex];
  const tabStopIndex =
    chosenOption && !chosenOption.disabled
      ? chosenIndex
      : options.findIndex((option) => !option.disabled);

  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (
      event.key !== "ArrowLeft" &&
      event.key !== "ArrowRight" &&
      event.key !== "ArrowUp" &&
      event.key !== "ArrowDown" &&
      event.key !== "Home" &&
      event.key !== "End"
    ) {
      return;
    }

    const segments = Array.from(
      event.currentTarget.querySelectorAll<HTMLButtonElement>(
        '[role="radio"]:not([disabled])',
      ),
    );
    if (segments.length === 0) return;

    const current = segments.indexOf(
      document.activeElement as HTMLButtonElement,
    );
    let nextIndex: number;
    switch (event.key) {
      case "ArrowLeft":
      case "ArrowUp":
        nextIndex = current <= 0 ? segments.length - 1 : current - 1;
        break;
      case "ArrowRight":
      case "ArrowDown":
        nextIndex = current === segments.length - 1 ? 0 : current + 1;
        break;
      case "Home":
        nextIndex = 0;
        break;
      default:
        nextIndex = segments.length - 1;
    }

    const next = segments[nextIndex];
    if (!next) return;

    event.preventDefault();
    next.focus();
    // Selection follows focus; index on the element because values may be numbers.
    const option = options[Number(next.dataset.index)];
    if (option) onValueChange(option.value);
  }

  const group = (
    <div
      {...rest}
      role="radiogroup"
      onKeyDown={handleKeyDown}
      className={cn("inline-flex items-center gap-1", className)}
    >
      {options.map((option, index) => {
        const chosen = index === chosenIndex;
        const chrome = variantClasses[variant];
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={chosen}
            data-index={index}
            tabIndex={index === tabStopIndex ? 0 : -1}
            disabled={option.disabled}
            onClick={() => onValueChange(option.value)}
            className={cn(
              "cursor-pointer select-none rounded-paper font-body font-medium",
              "transition duration-150 ease-paper motion-reduce:transition-none",
              "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
              "disabled:cursor-not-allowed disabled:text-faint",
              sizeClasses[size],
              mono && "font-mono font-normal tabular-nums",
              chosen ? chrome.chosen : chrome.resting,
              !option.disabled && chrome.hover,
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );

  if (label == null) return group;
  return (
    <div className="flex flex-col gap-1.5">
      <span className="font-body text-sm font-medium text-ink">{label}</span>
      {group}
    </div>
  );
}
